<script setup lang="ts">
import { computed } from "vue";
import { UiButton, UiIcon, UiInfoBox, UiSelect, UiText } from "@shopexperts/shared";
import type { BannedIp, BannedIpSource } from "@shopexperts/legacy-api";
import BanListSearchInput from "./BanListSearchInput.vue";
import BanListEmptyState from "./BanListEmptyState.vue";
import BanListLoadMore from "./BanListLoadMore.vue";
import BannedIpCard from "./BannedIpCard.vue";

const props = defineProps<{
    entries: BannedIp[];
    ruleOptions: { label: string; value: string }[];
    isLoading?: boolean;
    isFetching?: boolean;
    hasNextPage?: boolean;
    isFetchingNextPage?: boolean;
    hasFilters?: boolean;
    isBusy?: boolean;
}>();

const emit = defineEmits<{
    (e: "create"): void;
    (e: "remove", entry: BannedIp): void;
    (e: "filter-by-rule", ruleId: number): void;
    (e: "copy", entry: BannedIp): void;
    (e: "copy-all"): void;
    (e: "load-more"): void;
    (e: "reset-filters"): void;
}>();

const searchInput = defineModel<string>("searchInput", { required: true });
const sourceFilter = defineModel<BannedIpSource | "">("sourceFilter", { required: true });
const ruleFilter = defineModel<string>("ruleFilter", { required: true });

const sourceOptions: readonly { label: string; value: string }[] = [
    { label: "All sources", value: "" },
    { label: "Rule", value: "rule" },
    { label: "Manual", value: "manual" },
    { label: "Repeat", value: "repeat" },
];

const ruleSelectOptions = computed(() => [
    { label: "All rules", value: "" },
    ...props.ruleOptions,
]);

const copyAllLabel = computed(() => (
    props.entries.length === 1 ? "Copy 1 address" : `Copy ${props.entries.length} addresses`
));
</script>

<template>
    <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div class="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                <BanListSearchInput v-model="searchInput" placeholder="Search by IP or email..." />

                <div class="w-full sm:w-[170px]">
                    <UiSelect
                        v-model="sourceFilter"
                        :options="(sourceOptions as { label: string; value: string }[])"
                        placeholder="All sources"
                        size="md"
                    />
                </div>

                <div class="w-full sm:w-[200px]">
                    <UiSelect v-model="ruleFilter" :options="ruleSelectOptions" placeholder="All rules" size="md" />
                </div>

                <UiButton v-if="hasFilters" variant="ghost" label="Clear filters" @click="emit('reset-filters')" />
            </div>

            <div class="flex flex-wrap items-center gap-3">
                <UiButton
                    v-if="entries.length"
                    variant="outline"
                    :label="copyAllLabel"
                    @click="emit('copy-all')"
                >
                    <template #before_label>
                        <UiIcon name="copy" class="h-5 w-5" />
                    </template>
                </UiButton>

                <UiButton variant="primary" label="Add IP" @click="emit('create')">
                    <template #before_label>
                        <UiIcon name="plus-square" class="h-5 w-5 text-se-primary" />
                    </template>
                </UiButton>
            </div>
        </div>

        <UiInfoBox variant="info">
            This list is a registry, not a block list. The API never rejects a request because an address is on it —
            blocking is meant to happen at the edge, so export these addresses to Cloudflare.
        </UiInfoBox>

        <div v-if="isLoading" class="py-12 text-center">
            <UiText as="p" variant="p" class="text-se-gray-600" text="Loading IP registry..." />
        </div>

        <BanListEmptyState
            v-else-if="entries.length === 0"
            icon="globe-01"
            :title="hasFilters ? 'No addresses match these filters' : 'No addresses recorded'"
            :description="hasFilters
                ? 'Try another address, source or rule — or clear the filters.'
                : 'Addresses are recorded automatically whenever a rule blocks a submission, and can be added by hand for export.'"
            :action-label="hasFilters ? undefined : 'Add IP'"
            @action="emit('create')"
        />

        <template v-else>
            <BannedIpCard
                v-for="entry in entries"
                :key="entry.id"
                :entry="entry"
                :is-busy="isBusy"
                @remove="emit('remove', entry)"
                @copy="emit('copy', entry)"
                @filter-by-rule="(ruleId: number) => emit('filter-by-rule', ruleId)"
            />

            <BanListLoadMore
                :has-next-page="hasNextPage"
                :is-fetching-next-page="isFetchingNextPage"
                :is-refreshing="isFetching && !isLoading && !isFetchingNextPage"
                label="Load more addresses"
                @load-more="emit('load-more')"
            />
        </template>
    </div>
</template>
